/**
 * Arrondis et formats d'affichage — EF-CAL-008.
 *
 * Les conversions vers l'affichage vivent exclusivement ici : aucun kilomètre,
 * aucune minute ne circule hors de cette couche. Les valeurs enregistrées dans
 * les documents JSON ne sont jamais arrondies.
 */

import {
  formatLocalDateTime,
  isSameDay,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  toFields,
  type LocalDateTime,
} from '../core/local-datetime';
import { cardinal } from '../geo/geodesy';

/** Représentation d'une valeur non disponible, dotée d'un nom accessible. */
export const NOT_AVAILABLE = '—';
export const NOT_AVAILABLE_LABEL = 'non disponible';

function decimal(value: number, digits: number): string {
  return value.toFixed(digits).replace('.', ',');
}

/** Distance : kilomètres avec un chiffre après la virgule. */
export function formatDistance(meters: number): string {
  return `${decimal(meters / 1000, 1)} km`;
}

/**
 * Distance à la trace : mètres entiers en deçà de 1 km, kilomètres avec un
 * chiffre après la virgule au-delà.
 */
export function formatDistanceToRoute(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${decimal(meters / 1000, 1)} km`;
}

/** D+ : mètres, arrondis aux 10 m les plus proches. */
export function formatElevation(meters: number | null): string {
  if (meters === null) return NOT_AVAILABLE;
  return `${Math.round(meters / 10) * 10} m`;
}

/** Durée : heures et minutes, arrondies à la minute. */
export function formatDuration(seconds: number): string {
  const totalMinutes = Math.round(seconds / SECONDS_PER_MINUTE);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${String(minutes).padStart(2, '0')}`;
}

/** Heure estimée `HH:mm`, précédée de la date lorsque le jour diffère du départ. */
export function formatArrival(instant: LocalDateTime | null, startLocal: LocalDateTime | null): string {
  if (instant === null) return NOT_AVAILABLE;
  const fields = toFields(instant);
  const time = `${String(fields.hour).padStart(2, '0')}:${String(fields.minute).padStart(2, '0')}`;
  if (startLocal !== null && isSameDay(instant, startLocal)) return time;
  return `${String(fields.day).padStart(2, '0')}/${String(fields.month).padStart(2, '0')} ${time}`;
}

/** Date et heure complètes, pour la synthèse et les lignes d'étape. */
export function formatDateTime(instant: LocalDateTime): string {
  const fields = toFields(instant);
  return (
    `${String(fields.day).padStart(2, '0')}/${String(fields.month).padStart(2, '0')}/${fields.year} ` +
    `${String(fields.hour).padStart(2, '0')}:${String(fields.minute).padStart(2, '0')}`
  );
}

/** Cap : degrés entiers, suivis du point cardinal sur huit directions. */
export function formatBearing(degrees: number): string {
  const rounded = Math.round(degrees) % 360;
  return `${rounded}° ${cardinal(degrees)}`;
}

export function formatSpeed(kmh: number): string {
  return `${decimal(kmh, 1)} km/h`;
}

export function formatKilometers(meters: number): string {
  return decimal(meters / 1000, 1);
}

export function formatPercent(value: number, digits = 1): string {
  return `${decimal(value, digits)} %`;
}

export interface DurationBreakdown {
  readonly total: string;
  readonly riding: string;
  readonly pause: string;
  readonly sleep: string;
}

/**
 * Décomposition de la durée totale en temps de roulage, de pauses et de
 * sommeil, arrondie de sorte que **la somme des trois composantes affichées
 * soit exactement égale à la durée totale affichée**. Le reste d'arrondi est
 * reporté sur le temps de roulage.
 */
export function formatDurationBreakdown(
  totalSeconds: number,
  ridingSeconds: number,
  pauseSeconds: number,
  sleepSeconds: number,
): DurationBreakdown {
  const totalMinutes = Math.round(totalSeconds / SECONDS_PER_MINUTE);
  const pauseMinutes = Math.round(pauseSeconds / SECONDS_PER_MINUTE);
  const sleepMinutes = Math.round(sleepSeconds / SECONDS_PER_MINUTE);
  const ridingMinutes = totalMinutes - pauseMinutes - sleepMinutes;

  void ridingSeconds;

  return {
    total: formatMinutes(totalMinutes),
    riding: formatMinutes(ridingMinutes),
    pause: formatMinutes(pauseMinutes),
    sleep: formatMinutes(sleepMinutes),
  };
}

function formatMinutes(totalMinutes: number): string {
  const sign = totalMinutes < 0 ? '-' : '';
  const absolute = Math.abs(totalMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  if (hours === 0) return `${sign}${minutes} min`;
  return `${sign}${hours} h ${String(minutes).padStart(2, '0')}`;
}

/** Durée en secondes vers `HH:mm` pour les champs de saisie. */
export function secondsToHoursMinutes(seconds: number): string {
  const hours = Math.floor(seconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Valeur d'un champ `datetime-local`, sans fuseau ni décalage. */
export function toDateTimeLocalInput(instant: LocalDateTime): string {
  return formatLocalDateTime(instant).slice(0, 16);
}

/** Coordonnées copiées dans le presse-papiers, en degrés décimaux. */
export function formatCoordinate(lat: number, lon: number): string {
  return `${lat.toFixed(7)},${lon.toFixed(7)}`;
}

/** Surface d'un corridor de recherche, en kilomètres carrés. */
export function formatArea(squareMeters: number): string {
  return `${Math.round(squareMeters / 1_000_000).toLocaleString('fr-FR')} km²`;
}

export function formatCount(value: number): string {
  return value.toLocaleString('fr-FR');
}
